import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPencil,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';

const LaborByCoatManagement = () => {
  const [laborCosts, setLaborCosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [systemSettings, setSystemSettings] = useState({
    defaultCurrency: 'CAD',
    preferredUnit: 'sqm'
  });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [availableYears, setAvailableYears] = useState([]);
  const [filters, setFilters] = useState({
    year: '',
    coat_type: '',
    is_active: ''
  });

  const [formData, setFormData] = useState({
    coatType: 'prime',
    costPerUnit: '',
    unitOfMeasure: 'sqm',
    year: new Date().getFullYear(),
    currency: 'CAD',
    notes: '',
    isActive: true
  });

  const fetchAvailableYears = async () => {
    try {
      const response = await api.get('/labor-by-coat/years');
      if (response.data.success) {
        setAvailableYears(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching years:', err);
      setAvailableYears([]);
    }
  };

  useEffect(() => {
    fetchSystemSettings();
    fetchLaborCosts();
  }, [filters]);

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await api.get('/settings/categories');
      if (response.data.success) {
        const settings = response.data.data;
        const currency_units = settings.currency_units || {};
        const defaultCurrency = currency_units.default_currency?.value || 'CAD';
        const preferredUnit = currency_units.preferred_unit?.value || 'm²';
        
        // Convert m²/ft² to sqm/sqft
        const unitOfMeasure = preferredUnit.toLowerCase().includes('m²') || preferredUnit.toLowerCase().includes('m2') ? 'sqm' : 'sqft';
        
        setSystemSettings({
          defaultCurrency: defaultCurrency,
          preferredUnit: unitOfMeasure
        });
        
        // Update formData defaults
        setFormData(prev => ({
          ...prev,
          currency: defaultCurrency,
          unitOfMeasure: unitOfMeasure
        }));
      }
    } catch (err) {
      console.error('Error fetching system settings:', err);
      // Use defaults if settings fetch fails
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchLaborCosts = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();

      if (filters.year) params.append('year', filters.year);
      if (filters.coat_type) params.append('coat_type', filters.coat_type);
      if (filters.is_active !== '') params.append('is_active', filters.is_active);

      const response = await api.get(`/labor-by-coat?${params}`);

      if (response.data.success) {
        setLaborCosts(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch labor costs');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      coatType: 'prime',
      costPerUnit: '',
      unitOfMeasure: systemSettings.preferredUnit || 'sqm',
      year: new Date().getFullYear(),
      currency: systemSettings.defaultCurrency || 'CAD',
      notes: '',
      isActive: true
    });
    setEditingCost(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (editingCost) {
        await api.put(`/labor-by-coat/${editingCost.id}`, formData);
      } else {
        await api.post('/labor-by-coat', formData);
      }
      
      setShowModal(false);
      resetForm();
      fetchLaborCosts();
      fetchAvailableYears();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save labor cost');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cost) => {
    setEditingCost(cost);
    setFormData({
      coatType: cost.coatType,
      costPerUnit: cost.costPerUnit,
      unitOfMeasure: cost.unitOfMeasure || (cost.unitType ? (cost.unitType.toLowerCase().includes('m²') || cost.unitType.toLowerCase().includes('m2') ? 'sqm' : 'sqft') : 'sqm'),
      year: cost.year,
      currency: cost.currency || 'CAD',
      notes: cost.notes || '',
      isActive: cost.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (cost) => {
    if (window.confirm(`Delete labor cost for ${cost.coatType}?`)) {
      try {
        await api.delete(`/labor-by-coat/${cost.id}`);
        fetchLaborCosts();
        fetchAvailableYears();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete');
      }
    }
  };

  const getCoatTypeLabel = (type) => {
    const labels = {
      prime: 'Prime Coat',
      stripe: 'Stripe Coat',
      midcoat: 'Midcoat',
      topcoat: 'Topcoat'
    };
    return labels[type] || type;
  };

  const getCoatTypeColor = (type) => {
    const colors = {
      prime: 'bg-purple-100 text-purple-800',
      stripe: 'bg-orange-100 text-orange-800',
      midcoat: 'bg-blue-100 text-blue-800',
      topcoat: 'bg-green-100 text-green-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Labour Costs by Coating Type</h1>
        <p className="text-gray-600">
          Manage field application labor costs specific to each coat type (Prime, Stripe, Midcoat, Topcoat).
          Based on Excel "Field Application Cost per coat" table.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              name="year"
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Years</option>
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Coat Type</label>
            <select
              name="coat_type"
              value={filters.coat_type}
              onChange={(e) => setFilters({ ...filters, coat_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="prime">Prime</option>
              <option value="stripe">Stripe</option>
              <option value="midcoat">Midcoat</option>
              <option value="topcoat">Topcoat</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              name="is_active"
              value={filters.is_active}
              onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ year: '', coat_type: '', is_active: '' })}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Add Button */}
      <div className="mb-4">
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Labor Cost
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coat Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost per Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit of Measure
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {laborCosts.map((cost) => (
                  <tr key={cost.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getCoatTypeColor(cost.coatType)}`}>
                        {getCoatTypeLabel(cost.coatType)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ${parseFloat(cost.costPerUnit).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {cost.currency || 'CAD'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {cost.unitOfMeasure === 'sqm' ? 'm²' : cost.unitOfMeasure === 'sqft' ? 'ft²' : (cost.unitOfMeasure || (cost.unitType || 'm²'))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {cost.year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        cost.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {cost.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(cost)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faPencil} />
                      </button>
                      <button
                        onClick={() => handleDelete(cost)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingCost ? 'Edit Labor Cost' : 'Add New Labor Cost'}
              </h3>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coat Type *
                  </label>
                  <select
                    value={formData.coatType}
                    onChange={(e) => setFormData({ ...formData, coatType: e.target.value })}
                    required
                    disabled={!!editingCost}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    <option value="prime">Prime Coat</option>
                    <option value="stripe">Stripe Coat</option>
                    <option value="midcoat">Midcoat</option>
                    <option value="topcoat">Topcoat</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost per Unit *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.costPerUnit}
                    onChange={(e) => setFormData({ ...formData, costPerUnit: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 6.24"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency *
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit of Measure *
                  </label>
                  <select
                    value={formData.unitOfMeasure}
                    onChange={(e) => setFormData({ ...formData, unitOfMeasure: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sqm">sqm (m²)</option>
                    <option value="sqft">sqft (ft²)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year *
                  </label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional notes..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Active</label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : editingCost ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaborByCoatManagement;

